/**
 * Auth Router Service
 *
 * Handles communication with the Auth Router Lambda to determine
 * which regional Cognito User Pool a user should authenticate against.
 *
 * This enables multi-region authentication where users are routed to
 * their correct regional infrastructure based on their email address.
 */

import { BASE_URL } from '../externalConfig';

/**
 * Response from the Auth Router endpoint.
 */
export interface AuthRegionConfig {
  /** AWS region where the user's Cognito pool resides */
  region: string;
  /** Cognito User Pool ID */
  cognitoPoolId: string;
  /** Cognito App Client ID */
  clientId: string;
  /** Regional API base URL for subsequent API calls */
  apiBaseUrl: string;
  /** Cognito Identity Pool ID for AWS credentials (optional - used by mobile apps) */
  identityPoolId?: string;
}

/**
 * Cache for auth region configs to avoid repeated API calls
 * for the same email during a session.
 */
const authConfigCache: Map<string, { config: AuthRegionConfig; timestamp: number }> =
  new Map();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Fetches the regional auth configuration for a given email address.
 *
 * @param email - User's email address
 * @returns Regional auth configuration (Cognito pool, API base URL, etc.)
 *
 * @example
 * ```typescript
 * const config = await getAuthRegionConfig('user@example.com');
 * console.log(config.region); // 'eu-central-1'
 * console.log(config.cognitoPoolId); // 'eu-central-1_ABC123'
 * ```
 */
export async function getAuthRegionConfig(email: string): Promise<AuthRegionConfig> {
  const normalizedEmail = email.toLowerCase().trim();
  // Send the email with its ORIGINAL case (trim only). The Auth Router
  // lowercases internally for the DynamoDB lookup, so region resolution is
  // unchanged — but forwarding the raw case lets the (opt-in) Auth Router
  // diagnostic log capture exactly what the user typed on a sign-in /
  // forgot-password attempt. This is the only server-side point that can see
  // it (Cognito redacts the username in CloudTrail). Cache stays keyed by the
  // normalized email so case variants share one region result.
  const rawEmail = email.trim();

  // Check cache first
  const cached = authConfigCache.get(normalizedEmail);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.config;
  }

  try {
    // The Auth Router endpoint is accessible at /api/v1/auth/region
    // It's served through the same domain as the portal (via API Gateway)
    // Note: No credentials needed - this is a pre-auth endpoint that only returns
    // public regional configuration. Using 'omit' avoids CORS issues with wildcard origins.
    const response = await fetch(
      `${BASE_URL}/api/v1/auth/region?email=${encodeURIComponent(rawEmail)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'omit',
      },
    );

    if (!response.ok) {
      throw new Error('Failed to determine authentication region');
    }

    const config: AuthRegionConfig = await response.json();

    // Cache the result
    authConfigCache.set(normalizedEmail, {
      config,
      timestamp: Date.now(),
    });

    return config;
  } catch (error) {
    // Re-throw to let the caller handle it (graceful degradation)
    throw error;
  }
}

/**
 * Clears the auth config cache.
 * Call this on sign-out to ensure fresh config on next sign-in.
 */
export function clearAuthConfigCache(): void {
  authConfigCache.clear();
}

/**
 * Gets the cached auth config for an email if available.
 * Returns null if not cached or cache expired.
 */
export function getCachedAuthConfig(email: string): AuthRegionConfig | null {
  const normalizedEmail = email.toLowerCase().trim();
  const cached = authConfigCache.get(normalizedEmail);

  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.config;
  }

  return null;
}

/**
 * Checks if the Auth Router feature is enabled.
 *
 * The Auth Router is considered enabled if the BASE_URL is configured
 * and we're not in a development environment that should bypass it.
 *
 * @returns true if Auth Router should be used
 */
export function isAuthRouterEnabled(): boolean {
  // Auth Router is enabled when we have a valid BASE_URL
  // In development (localhost), it may be disabled
  try {
    return Boolean(BASE_URL && !BASE_URL.includes('localhost'));
  } catch {
    return false;
  }
}
